import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ServiceChangeRequestService,
  ServiceFormField,
} from '../../../../core/services/service-change-request.service';
import { ToastService } from '@irembo-andela/irembogov3-common';

type ConfigType = 'STATIC' | 'DYNAMIC';
type ConfigMode = 'SINGLE' | 'COMBINED';
type AttributeType = 'STRING' | 'NUMBER' | 'DATE';
type Comparator = 'EQ' | 'NEQ' | 'GT' | 'GTE' | 'LT' | 'LTE' | 'CONTAINS';

export interface ReceiptNameCondition {
  attribute: string | null;
  attributeType: AttributeType;
  comparator: Comparator;
  value: string;
}

export interface ReceiptNameRule {
  receiptNameSourceFields: string[];
  receiptNameFallbackSourceField?: string;
  conditions?: ReceiptNameCondition[];
}

export interface DynamicScenario {
  fieldMode: ConfigMode;
  // single field helper — kept in sync with sourceFields[0]
  selectedSingleField: string | null;
  sourceFields: string[];
  conditions: ReceiptNameCondition[];
  newFieldToAdd: string | null;
}

const newCondition = (): ReceiptNameCondition => ({
  attribute: null,
  attributeType: 'STRING',
  comparator: 'EQ',
  value: '',
});

@Component({
  selector: 'irembogov-configuration-receipt-name',
  templateUrl: './configuration-receipt-name.component.html',
  styleUrls: ['./configuration-receipt-name.component.scss'],
})
export class ConfigurationReceiptNameComponent implements OnInit, OnChanges {
  @Input() formFields!: any;
  @Input() changeRequestJsonWorkflowData!: Record<string, unknown>;
  @Output() workflowUpdated = new EventEmitter<any>();

  availableFields: ServiceFormField[] = [];
  unselectedFields: ServiceFormField[] = [];
  scenarioUnselectedFields: ServiceFormField[][] = [];

  comparatorOptions = [
    { label: 'Equal To', value: 'EQ' },
    { label: 'Not Equal To', value: 'NEQ' },
    { label: 'Contains', value: 'CONTAINS' },
    { label: 'Greater Than', value: 'GT' },
    { label: 'Greater Than or Equal', value: 'GTE' },
    { label: 'Less Than', value: 'LT' },
    { label: 'Less Than or Equal', value: 'LTE' },
  ];

  attributeTypeOptions = [
    { label: 'Text', value: 'STRING' },
    { label: 'Number', value: 'NUMBER' },
    { label: 'Date', value: 'DATE' },
  ];

  fieldModeOptions = [
    { label: 'Single field', value: 'SINGLE' },
    { label: 'Combined fields', value: 'COMBINED' },
  ];

  // Config type
  configType: ConfigType = 'STATIC';

  // Static mode
  configMode: ConfigMode = 'SINGLE';
  selectedField: string | null = null;
  combinedFields: string[] = [];
  newFieldToAdd: string | null = null;
  fallbackEnabled = false;
  fallbackField: string | null = null;

  // Dynamic mode — shared fallback across all rules
  scenarios: DynamicScenario[] = [];
  dynamicFallbackEnabled = false;
  dynamicFallbackField: string | null = null;

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.parseFormFields();
    this.loadFromWorkflow();
    this.updateUnselectedFields();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formFields']?.currentValue) {
      this.parseFormFields();
    }
    if (
      changes['changeRequestJsonWorkflowData']?.currentValue &&
      !changes['changeRequestJsonWorkflowData'].isFirstChange()
    ) {
      this.loadFromWorkflow();
    }
  }

  // ─── trackBy ────────────────────────────────────────────────────────────────

  trackByIndex(index: number): number {
    return index;
  }

  // ─── Config type ────────────────────────────────────────────────────────────

  setConfigType(type: ConfigType): void {
    this.configType = type;
  }

  setConfigMode(mode: ConfigMode): void {
    this.configMode = mode;
  }

  // ─── Static / Combined ordered list ─────────────────────────────────────────

  private updateUnselectedFields(): void {
    // Global unselected fields
    this.unselectedFields = this.availableFields.filter(
      f => !this.combinedFields.includes(f.fieldName)
    );

    // Per-scenario unselected fields
    this.scenarioUnselectedFields = this.scenarios.map(scenario =>
      this.availableFields.filter(
        f => !scenario.sourceFields.includes(f.fieldName)
      )
    );
  }

  getFieldLabel(fieldName: string): string {
    return (
      this.availableFields.find(f => f.fieldName === fieldName)?.fieldLabel ??
      fieldName
    );
  }

  addCombinedField(item: ServiceFormField | null): void {
    const fieldName = item?.fieldName ?? null;
    setTimeout(() => {
      if (fieldName && !this.combinedFields.includes(fieldName)) {
        this.combinedFields = [...this.combinedFields, fieldName];
        this.updateUnselectedFields();
      }
      this.newFieldToAdd = null;
    }, 0);
  }

  removeCombinedField(index: number): void {
    this.combinedFields = this.combinedFields.filter((_, i) => i !== index);
  }

  moveFieldUp(index: number): void {
    if (index === 0) return;
    const fields = [...this.combinedFields];
    [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
    this.combinedFields = fields;
  }

  moveFieldDown(index: number): void {
    if (index === this.combinedFields.length - 1) return;
    const fields = [...this.combinedFields];
    [fields[index], fields[index + 1]] = [fields[index + 1], fields[index]];
    this.combinedFields = fields;
  }

  // ─── Dynamic scenarios ───────────────────────────────────────────────────────

  addScenario(): void {
    this.scenarios = [
      ...this.scenarios,
      {
        fieldMode: 'SINGLE',
        selectedSingleField: null,
        sourceFields: [],
        conditions: [newCondition()],
        newFieldToAdd: null,
      },
    ];
    this.updateUnselectedFields();
  }

  removeScenario(index: number): void {
    this.scenarios = this.scenarios.filter((_, i) => i !== index);
    this.updateUnselectedFields();
  }

  onScenarioFieldModeChange(scenario: DynamicScenario, mode: ConfigMode): void {
    scenario.fieldMode = mode;
    scenario.sourceFields = [];
    scenario.selectedSingleField = null;
  }

  onScenarioSingleFieldChange(
    scenario: DynamicScenario,
    fieldName: string | null
  ): void {
    scenario.selectedSingleField = fieldName;
    scenario.sourceFields = fieldName ? [fieldName] : [];
  }

  addCondition(scenario: DynamicScenario): void {
    scenario.conditions = [...scenario.conditions, newCondition()];
  }

  removeCondition(scenario: DynamicScenario, conditionIndex: number): void {
    scenario.conditions = scenario.conditions.filter(
      (_, ci) => ci !== conditionIndex
    );
  }

  addScenarioField(scenarioIndex: number): void {
    const scenario = this.scenarios[scenarioIndex];
    if (!scenario?.newFieldToAdd) return;
    this.scenarios = this.scenarios.map((s, i) =>
      i === scenarioIndex
        ? { ...s, sourceFields: [...s.sourceFields, s.newFieldToAdd!] }
        : s
    );
    setTimeout(() => {
      this.scenarios = this.scenarios.map((s, i) =>
        i === scenarioIndex ? { ...s, newFieldToAdd: null } : s
      );
      this.updateUnselectedFields();
    }, 0);
    this.updateUnselectedFields();
  }

  removeScenarioField(scenarioIndex: number, fieldIndex: number): void {
    this.scenarios = this.scenarios.map((s, i) =>
      i === scenarioIndex
        ? {
            ...s,
            sourceFields: s.sourceFields.filter((_, fi) => fi !== fieldIndex),
          }
        : s
    );
    this.updateUnselectedFields();
  }

  // ─── Save / Continue ─────────────────────────────────────────────────────────

  onSave(): void {
    this.emitUpdate();
    this.toastService.show({
      body: 'Receipt name configuration saved successfully',
      type: 'success',
    });
  }

  onSaveDynamic(): void {
    this.emitUpdate();
    this.toastService.show({
      body: 'Dynamic receipt name rules saved successfully',
      type: 'success',
    });
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private parseFormFields(): void {
    if (this.formFields) {
      this.availableFields = this.serviceChangeRequestService.parseFormFields(
        this.formFields
      );
    }
  }

  private loadFromWorkflow(): void {
    const workflow =
      (this.changeRequestJsonWorkflowData?.['workflow'] as any[]) || [];
    const action = this.findBillIdGenerationAction(workflow);
    const rules: ReceiptNameRule[] =
      action?.args?.receiptMetadata?.receiptNameRules ?? [];

    if (rules.length === 0) return;

    const hasDynamicConditions = rules.some(
      r => r.conditions && r.conditions.length > 0
    );

    if (hasDynamicConditions) {
      this.configType = 'DYNAMIC';
      // Use first rule's fallback as the shared dynamic fallback
      const firstFallback =
        rules.find(r => r.receiptNameFallbackSourceField)
          ?.receiptNameFallbackSourceField ?? null;
      this.dynamicFallbackEnabled = !!firstFallback;
      this.dynamicFallbackField = firstFallback;

      this.scenarios = rules.map(r => {
        const fields = r.receiptNameSourceFields ?? [];
        const isMulti = fields.length > 1;
        return {
          fieldMode: isMulti ? 'COMBINED' : ('SINGLE' as ConfigMode),
          selectedSingleField: isMulti ? null : fields[0] ?? null,
          sourceFields: fields,
          conditions: (r.conditions ?? []).map(c => ({
            attribute: c.attribute ?? null,
            attributeType: (c.attributeType ?? 'STRING') as AttributeType,
            comparator: (c.comparator ?? 'EQ') as Comparator,
            value: c.value ?? '',
          })),
          newFieldToAdd: null,
        };
      });
    } else {
      this.configType = 'STATIC';
      const primaryRule = rules[0];
      const fields = primaryRule.receiptNameSourceFields ?? [];
      if (fields.length > 1) {
        this.configMode = 'COMBINED';
        this.combinedFields = fields;
      } else {
        this.configMode = 'SINGLE';
        this.selectedField = fields[0] ?? null;
      }
      this.fallbackEnabled = !!primaryRule.receiptNameFallbackSourceField;
      this.fallbackField = primaryRule.receiptNameFallbackSourceField ?? null;
    }
  }

  private buildRules(): ReceiptNameRule[] {
    if (this.configType === 'DYNAMIC') {
      return this.scenarios.map(s => {
        const rule: ReceiptNameRule = {
          receiptNameSourceFields: s.sourceFields,
          conditions: s.conditions.map(c => ({
            attribute: c.attribute ?? '',
            attributeType: c.attributeType,
            comparator: c.comparator,
            value: c.value,
          })),
        };
        if (this.dynamicFallbackEnabled && this.dynamicFallbackField) {
          rule.receiptNameFallbackSourceField = this.dynamicFallbackField;
        }
        return rule;
      });
    }

    const sourceFields =
      this.configMode === 'SINGLE'
        ? this.selectedField
          ? [this.selectedField]
          : []
        : this.combinedFields;

    const rule: ReceiptNameRule = { receiptNameSourceFields: sourceFields };
    if (this.fallbackEnabled && this.fallbackField) {
      rule.receiptNameFallbackSourceField = this.fallbackField;
    }
    return [rule];
  }

  private emitUpdate(): void {
    const workflow: any[] = JSON.parse(
      JSON.stringify(
        (this.changeRequestJsonWorkflowData?.['workflow'] as any[]) || []
      )
    );
    this.updateBillIdActionInWorkflow(workflow, this.buildRules());
    this.workflowUpdated.emit(workflow);
  }

  private updateBillIdActionInWorkflow(
    workflow: any[],
    rules: ReceiptNameRule[]
  ): void {
    for (const step of workflow) {
      if (this.updateActionInStep(step, rules)) return;
    }
  }

  private updateActionInStep(step: any, rules: ReceiptNameRule[]): boolean {
    if (step?.breakingAction?.actionType === 'BILL_ID_GENERATION') {
      step.breakingAction.args = {
        ...step.breakingAction.args,
        receiptMetadata: { receiptNameRules: rules },
      };
      return true;
    }
    if (Array.isArray(step?.breakingActions)) {
      const action = step.breakingActions.find(
        (a: any) => a.actionType === 'BILL_ID_GENERATION'
      );
      if (action) {
        action.args = {
          ...action.args,
          receiptMetadata: { receiptNameRules: rules },
        };
        return true;
      }
    }
    if (Array.isArray(step?.nonBreakingActions)) {
      const action = step.nonBreakingActions.find(
        (a: any) => a.actionType === 'BILL_ID_GENERATION'
      );
      if (action) {
        action.args = {
          ...action.args,
          receiptMetadata: { receiptNameRules: rules },
        };
        return true;
      }
    }
    if (step?.endStateOne && this.updateActionInStep(step.endStateOne, rules))
      return true;
    if (step?.endStateTwo && this.updateActionInStep(step.endStateTwo, rules))
      return true;
    return false;
  }

  private findBillIdGenerationAction(workflow: any[]): any | null {
    for (const step of workflow) {
      const found = this.searchActionInStep(step);
      if (found) return found;
    }
    return null;
  }

  private searchActionInStep(step: any): any | null {
    if (step?.breakingAction?.actionType === 'BILL_ID_GENERATION')
      return step.breakingAction;
    if (Array.isArray(step?.breakingActions)) {
      const found = step.breakingActions.find(
        (a: any) => a.actionType === 'BILL_ID_GENERATION'
      );
      if (found) return found;
    }
    if (Array.isArray(step?.nonBreakingActions)) {
      const found = step.nonBreakingActions.find(
        (a: any) => a.actionType === 'BILL_ID_GENERATION'
      );
      if (found) return found;
    }
    if (step?.endStateOne) {
      const found = this.searchActionInStep(step.endStateOne);
      if (found) return found;
    }
    if (step?.endStateTwo) {
      const found = this.searchActionInStep(step.endStateTwo);
      if (found) return found;
    }
    return null;
  }
}
